/**
 * 埋点相关的 Vue 自定义指令
 * 使用方式：
 * v-track-click="配置对象"
 * v-track-pv="配置对象"
 * v-track-event="配置对象"
 */

import tracking from '@/utils/tracking'

/**
 * 点击事件埋点指令
 * 
 * @example
 * <div v-track-click="{ elementId: 'game_card', extraData: { gameId: item.game_id } }">
 * 
 * @example 使用函数返回配置（可以访问item数据）
 * <div v-track-click="(el, item) => ({ 
 *   elementId: 'game_card',
 *   elementText: item.name,
 *   extraData: { gameId: item.id }
 * })">
 */
export const vTrackClick = {
  mounted(el, binding, vnode) {
    // 保存点击处理函数引用，用于后续解绑
    el._trackClickHandler = function() {
      let config = binding.value
      
      // 如果传入的是函数，执行函数获取配置
      if (typeof config === 'function') {
        config = config(el, vnode.props)
      }
      
      // 默认配置
      const defaultConfig = {
        elementId: el.id || '',
        elementType: el.tagName.toLowerCase(),
        elementName: el.className || '',
        elementText: el.innerText?.substring(0, 50) || '', // 限制文本长度
        pageName: '',
      }
      
      // 合并配置
      const trackingConfig = {
        ...defaultConfig,
        ...config,
      }
      
      // 发送埋点
      tracking.trackClick(trackingConfig)
    }
    
    // 绑定点击事件
    el.addEventListener('click', el._trackClickHandler)
  },
  
  // 指令卸载时移除事件监听
  unmounted(el) {
    if (el._trackClickHandler) {
      el.removeEventListener('click', el._trackClickHandler)
      delete el._trackClickHandler
    }
  },
  
  // 指令更新时重新绑定（可选）
  updated() {
    // 如果配置发生变化，可以在这里处理
    // 目前的实现会在每次点击时重新获取配置，所以不需要特殊处理
  }
}

/**
 * 曝光埋点指令（元素进入可视区域时触发）
 * 
 * @example
 * <div v-track-exposure="{ elementId: 'banner', extraData: { bannerId: '123' } }">
 */
export const vTrackExposure = {
  mounted(el, binding) {
    // 使用 IntersectionObserver 监听元素是否进入视口
    const options = {
      root: null, // 视口
      rootMargin: '0px',
      threshold: 0.5 // 元素50%可见时触发
    }
    
    el._trackExposureObserver = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // 元素进入视口且未被标记为已曝光
        if (entry.isIntersecting && !el._trackExposed) {
          let config = binding.value
          
          // 如果传入的是函数，执行函数获取配置
          if (typeof config === 'function') {
            config = config(el)
          }
          
          // 默认配置
          const defaultConfig = {
            elementId: el.id || '',
            elementType: el.tagName.toLowerCase(),
            elementName: el.className || '',
          }
          
          // 合并配置并发送埋点
          const trackingConfig = {
            eventType: 'exposure',
            ...defaultConfig,
            ...config,
          }
          
          // 使用通用事件追踪
          tracking.trackEvent({
            eventName: 'element_exposure',
            eventCategory: 'exposure',
            eventAction: 'view',
            eventLabel: trackingConfig.elementId,
            ...trackingConfig,
          })
          
          // 标记为已曝光（避免重复上报）
          el._trackExposed = true
          
          // 如果配置了 once: true，则只上报一次后停止观察
          if (config?.once !== false) {
            el._trackExposureObserver.disconnect()
          }
        }
      })
    }, options)
    
    // 开始观察
    el._trackExposureObserver.observe(el)
  },
  
  unmounted(el) {
    if (el._trackExposureObserver) {
      el._trackExposureObserver.disconnect()
      delete el._trackExposureObserver
      delete el._trackExposed
    }
  }
}

/**
 * 自定义事件埋点指令
 * 
 * @example
 * <div v-track-event="{ 
 *   trigger: 'mouseenter',
 *   eventName: 'hover_game_card',
 *   extraData: { gameId: '123' }
 * }">
 */
export const vTrackEvent = {
  mounted(el, binding) {
    let config = binding.value
    
    // 获取触发事件类型，默认为 click
    const triggerEvent = config.trigger || 'click'
    
    // 保存事件处理函数
    el._trackEventHandler = function(event) {
      let currentConfig = binding.value
      
      // 如果传入的是函数，执行函数获取配置
      if (typeof currentConfig === 'function') {
        currentConfig = currentConfig(el, event)
      }
      
      // 发送自定义事件埋点
      tracking.trackEvent({
        eventName: currentConfig.eventName || 'custom_event',
        eventCategory: currentConfig.eventCategory || 'interaction',
        eventAction: currentConfig.eventAction || triggerEvent,
        eventLabel: currentConfig.eventLabel || '',
        eventValue: currentConfig.eventValue || 0,
        extraData: currentConfig.extraData || {},
      })
    }
    
    // 绑定事件
    el.addEventListener(triggerEvent, el._trackEventHandler)
    
    // 保存事件类型，用于卸载时移除
    el._trackEventType = triggerEvent
  },
  
  unmounted(el) {
    if (el._trackEventHandler && el._trackEventType) {
      el.removeEventListener(el._trackEventType, el._trackEventHandler)
      delete el._trackEventHandler
      delete el._trackEventType
    }
  }
}

/**
 * 停留时长埋点指令（记录用户在元素上的停留时间）
 * 
 * @example
 * <div v-track-duration="{ elementId: 'game_detail', minDuration: 1000 }">
 */
export const vTrackDuration = {
  mounted(el, binding) {
    let startTime = null
    let config = binding.value || {}
    
    // 鼠标进入时记录开始时间
    el._trackDurationEnterHandler = function() {
      startTime = Date.now()
    }
    
    // 鼠标离开时计算停留时长并上报
    el._trackDurationLeaveHandler = function() {
      if (startTime) {
        const duration = Date.now() - startTime
        const minDuration = config.minDuration || 0
        
        // 只有停留时长大于最小时长才上报
        if (duration >= minDuration) {
          tracking.trackEvent({
            eventName: 'element_duration',
            eventCategory: 'duration',
            eventAction: 'stay',
            eventLabel: config.elementId || el.id || '',
            eventValue: duration,
            extraData: {
              duration,
              elementId: config.elementId || el.id || '',
              ...config.extraData,
            },
          })
        }
        
        startTime = null
      }
    }
    
    el.addEventListener('mouseenter', el._trackDurationEnterHandler)
    el.addEventListener('mouseleave', el._trackDurationLeaveHandler)
  },
  
  unmounted(el) {
    if (el._trackDurationEnterHandler) {
      el.removeEventListener('mouseenter', el._trackDurationEnterHandler)
      delete el._trackDurationEnterHandler
    }
    if (el._trackDurationLeaveHandler) {
      el.removeEventListener('mouseleave', el._trackDurationLeaveHandler)
      delete el._trackDurationLeaveHandler
    }
  }
}

/**
 * 默认导出所有埋点指令
 */
export default {
  install(app) {
    // 注册所有埋点指令
    app.directive('track-click', vTrackClick)
    app.directive('track-exposure', vTrackExposure)
    app.directive('track-event', vTrackEvent)
    app.directive('track-duration', vTrackDuration)
  }
}
